import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  MapPin, Plus, Leaf, Droplets, Home,
  TrendingUp, Clock, Eye, Settings, Map, AlertTriangle,
} from 'lucide-react'
import { getPaddocks, getPaddockSummary, getGrazingTracker, getDroneComposition } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import StatCard from '../../components/StatCard'
import GrazingVerificationMap from '../../components/GrazingVerificationMap'

// Live grazing state comes from the grazing tracker (derived from the manager's
// locked calendar + satellite), NOT paddock_status.current_status — that cache
// is never written by the calendar-lock flow, so it reads "resting" forever.
const STATE_BADGE = {
  grazed_recently: { color: 'bg-green-100 text-green-800', label: 'Grazing', icon: '🐄', top: 'border-t-green-500' },
  ready:           { color: 'bg-blue-100 text-blue-800', label: 'Ready', icon: '✓', top: 'border-t-blue-400' },
  attention:       { color: 'bg-red-100 text-red-800', label: 'Needs a look', icon: '⚠', top: 'border-t-red-400' },
  recovering:      { color: 'bg-yellow-100 text-yellow-800', label: 'Resting', icon: '💤', top: 'border-t-amber-400' },
}
const badgeFor = (state) => STATE_BADGE[state] || STATE_BADGE.recovering

export default function PaddockList({ ranchId }) {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')

  const { data: paddocks, isLoading } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })
  const { data: summary } = useQuery({
    queryKey: ['paddockSummary', ranchId],
    queryFn: () => getPaddockSummary(ranchId),
  })
  const { data: tracker } = useQuery({
    queryKey: ['grazingTracker', ranchId],
    queryFn: () => getGrazingTracker(ranchId),
    enabled: !!ranchId,
    refetchInterval: 5 * 60_000,
  })
  // Drone composition (latest flight + trend vs previous). Optional layer:
  // a ranch with no flights renders the same cards without the drone line.
  const { data: droneComp } = useQuery({
    queryKey: ['droneComposition', ranchId],
    queryFn: () => getDroneComposition(ranchId),
    enabled: !!ranchId,
    staleTime: 10 * 60_000,
  })

  const byId = useMemo(() => {
    const m = {}
    ;(tracker?.paddocks || []).forEach(t => { m[t.paddock_id] = t })
    return m
  }, [tracker])
  const stateOf = (p) => byId[p.id]?.utilization_state || 'recovering'

  const counts = useMemo(() => {
    const c = { total: paddocks?.length || 0, grazing: 0, resting: 0, ready: 0, attention: 0 }
    ;(paddocks || []).forEach(p => {
      const s = byId[p.id]?.utilization_state
      if (s === 'grazed_recently') c.grazing++
      else if (s === 'ready') c.ready++
      else if (s === 'attention') c.attention++
      else c.resting++
    })
    return c
  }, [paddocks, byId])

  const anyGrazing = (paddocks || []).some(p => stateOf(p) === 'grazed_recently')
  const filtered = (paddocks || []).filter(p => {
    if (!statusFilter) return true
    const s = stateOf(p)
    if (statusFilter === 'grazing') return s === 'grazed_recently'
    if (statusFilter === 'resting') return s === 'recovering'
    if (statusFilter === 'attention') return s === 'attention'
    return true
  })

  return (
    <div className="p-6">
      <PageHeader
        title="Paddocks"
        subtitle={`${counts.total} paddocks · ${summary?.total_area_ha?.toFixed(1) || 0} ha total`}
        icon={MapPin}
        actions={
          <>
            <Link to="/paddocks/map" className="btn bg-earth-100 text-earth-700 hover:bg-earth-200">
              <Map className="h-4 w-4 mr-2" /> Map View
            </Link>
            <Link
              to="/paddocks/reconfigure"
              className={`btn ${anyGrazing ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
              onClick={e => { if (anyGrazing) e.preventDefault() }}
              title={anyGrazing ? 'End all grazing before reconfiguring' : 'Change paddock layout'}
            >
              <Settings className="h-4 w-4 mr-2" /> Reconfigure
            </Link>
            <Link to="/paddocks/new" className="btn bg-green-600 text-white hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" /> Add Paddock
            </Link>
          </>
        }
      />

      {/* Ranch grazing verification — the spatial overview, front and centre. */}
      {ranchId && <GrazingVerificationMap ranchId={ranchId} />}

      {/* Live status summary (from the last locked rotation, not the stale cache). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 mb-6">
        <StatCard title="Grazing" value={counts.grazing} icon={Leaf} color="green" />
        <StatCard title="Resting" value={counts.resting} icon={Clock} color="amber" />
        <StatCard title="Ready to Graze" value={counts.ready} icon={TrendingUp} color="blue" />
        <StatCard title="Needs a Look" value={counts.attention} icon={AlertTriangle} color={counts.attention ? 'red' : 'primary'} />
      </div>

      {/* Filter */}
      <div className="section-card p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            ['', `All (${counts.total})`, 'bg-gray-900 text-white', 'bg-gray-100 text-gray-700 hover:bg-gray-200'],
            ['grazing', `Grazing (${counts.grazing})`, 'bg-green-600 text-white', 'bg-green-100 text-green-700 hover:bg-green-200'],
            ['resting', `Resting (${counts.resting})`, 'bg-yellow-600 text-white', 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'],
            ...(counts.attention ? [['attention', `Needs a look (${counts.attention})`, 'bg-red-600 text-white', 'bg-red-100 text-red-700 hover:bg-red-200']] : []),
          ].map(([key, label, on, off]) => (
            <button key={key} onClick={() => setStatusFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === key ? on : off}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Loading paddocks...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No paddocks {statusFilter ? 'in this state' : 'found'}</p>
          {!statusFilter && (
            <Link to="/paddocks/new" className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Plus className="h-5 w-5" /> Add First Paddock
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((paddock) => {
            const t = byId[paddock.id]
            const badge = badgeFor(t?.utilization_state)
            return (
              <div key={paddock.id}
                className={`bg-white rounded-xl shadow-sm border border-earth-200 hover:shadow-md transition-shadow border-t-4 ${badge.top}`}>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{paddock.code}</h3>
                      <p className="text-sm text-gray-600">{paddock.name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                      {badge.icon} {badge.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <MapPin className="h-4 w-4" /> {paddock.area_ha ? `${paddock.area_ha} ha` : 'N/A'}
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="h-4 w-4" /> {t?.rest_days ?? paddock.status?.days_rested ?? 0} days rest
                    </div>
                    {paddock.has_water && (
                      <div className="flex items-center gap-1 text-blue-600"><Droplets className="h-4 w-4" /> Water</div>
                    )}
                    {paddock.has_kraal && (
                      <div className="flex items-center gap-1 text-amber-600"><Home className="h-4 w-4" /> Kraal</div>
                    )}
                  </div>

                  {/* Drone survey line: bush/grass composition + trend from
                      the latest flight. Skeleton em-dashes when the ranch has
                      flights but this paddock wasn't covered; hidden entirely
                      when no flight exists yet. */}
                  {droneComp?.observation_date && (() => {
                    const d = droneComp.paddocks?.[paddock.code]
                    const fmt = (v) => (v == null ? '—' : `${Math.round(v)}%`)
                    const arrow = (delta) => {
                      if (delta == null || Math.abs(delta) < 0.5) return null
                      const up = delta > 0
                      return (
                        <span className={up ? 'text-red-600' : 'text-green-700'}>
                          {up ? '▲' : '▼'}{Math.abs(delta).toFixed(1)}
                        </span>
                      )
                    }
                    return (
                      <p className="text-xs text-gray-500 mb-3"
                         title={`Drone survey ${d?.observation_date || droneComp.observation_date}. Bush = woody canopy ≥0.5 m (LiDAR). Arrows = change vs previous flight (red = more bush).`}>
                        🚁 Bush {fmt(d?.woody_chm_pct)} {arrow(d?.deltas?.woody_chm_pct)}
                        {' · '}Grass {fmt(d?.grass_pct)}
                        {' · '}Bare {fmt(d?.bare_pct)}
                      </p>
                    )
                  })()}

                  {t?.last_grazed && (
                    <p className="text-xs text-gray-500 mb-3">
                      Last grazed {new Date(t.last_grazed).toLocaleDateString()}
                      {t.days_since_grazed != null && <> &middot; {t.days_since_grazed}d ago</>}
                      {t.recovery_pct != null && <> &middot; {t.recovery_pct}% recovered</>}
                      {t.satellite_verified && <> &middot; <span className="text-green-700">satellite&#8209;verified</span></>}
                    </p>
                  )}

                  {t?.utilization_state === 'grazed_recently' && t?.current_herd && (
                    <div className="mb-3 p-2 bg-green-50 rounded-lg text-sm text-green-800">
                      <span className="font-medium">{t.cattle_count || 0}</span> cattle ({t.current_herd})
                    </div>
                  )}

                  <button onClick={() => navigate(`/paddocks/${paddock.id}`)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                    <Eye className="h-4 w-4" /> View Details
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
